import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, ArrowRight, ArrowLeft, Check, Globe, Gift, Sparkles, Users, MessageCircle, Copy, Crown, Shield, Lock, ChevronLeft, ChevronRight, UserPlus, Calendar, Repeat, Heart } from 'lucide-react';
type OnboardingStep = 'landing' | 'auth' | 'identity' | 'referral' | 'language' | 'reward' | 'packet' | 'explain' | 'misl' | 'share' | 'complete';
interface TreasuryOnboardingProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  /** When the modal is opened from an invite link, the inviter's name. */
  invitedBy?: string;
  /** When opened from an invite link, the misl the inviter belongs to. */
  invitedMisl?: string;
  /** When opened from an invite link, the raw referral code from the URL. */
  inviteCode?: string;
}
export function TreasuryOnboarding({
  isOpen,
  onClose,
  onComplete,
  invitedBy,
  invitedMisl,
  inviteCode
}: TreasuryOnboardingProps) {
  const [step, setStep] = useState<OnboardingStep>('landing');
  const [email, setEmail] = useState('');
  const [language, setLanguage] = useState<'en' | 'pa' | 'both'>('en');
  const [referralCode, setReferralCode] = useState('');
  const [packetState, setPacketState] = useState<'sealed' | 'opening' | 'revealed'>('sealed');
  const [mislView, setMislView] = useState<'choose' | 'browse' | 'confirm-invite' | 'confirm-later'>('choose');
  const [showSkipShareConfirm, setShowSkipShareConfirm] = useState(false);
  const [selectedMisl, setSelectedMisl] = useState<string | null>(null);
  const misls = [
  // Homeland / Region
  {
    name: 'Lahore',
    leader: '@lahore_s',
    members: '2,100',
    xp: '720k',
    tag: 'Homeland · Founding city',
    region: 'Punjab · Pakistan'
  }, {
    name: 'Amritsar',
    leader: '@golden_s',
    members: '1,360',
    xp: '510k',
    tag: 'Spiritual heart',
    region: 'Punjab · India'
  }, {
    name: 'Majha',
    leader: '@majha_s',
    members: '960',
    xp: '370k',
    tag: 'Heartland',
    region: 'Central Punjab'
  }, {
    name: 'Doaba',
    leader: '@doaba_k',
    members: '780',
    xp: '300k',
    tag: 'Rivers between',
    region: 'Beas–Sutlej'
  }, {
    name: 'Malwa',
    leader: '@malwa_s',
    members: '820',
    xp: '320k',
    tag: 'Southern strength',
    region: 'South Punjab'
  },
  // Diaspora / City
  {
    name: 'Brampton',
    leader: '@brampton_s',
    members: '1,420',
    xp: '460k',
    tag: 'Diaspora · Canada',
    region: 'Ontario'
  }, {
    name: 'Surrey',
    leader: '@surrey_s',
    members: '1,180',
    xp: '410k',
    tag: 'Diaspora · Canada',
    region: 'British Columbia'
  }, {
    name: 'Southall',
    leader: '@southall_s',
    members: '980',
    xp: '360k',
    tag: 'Diaspora · UK',
    region: 'London'
  }, {
    name: 'Toronto',
    leader: '@toronto_s',
    members: '1,260',
    xp: '440k',
    tag: 'Diaspora · Canada',
    region: 'Ontario'
  }, {
    name: 'Vancouver',
    leader: '@van_s',
    members: '1,040',
    xp: '390k',
    tag: 'Diaspora · Canada',
    region: 'British Columbia'
  },
  // Family / Clan
  {
    name: 'Sandhu',
    leader: '@sandhu_s',
    members: '1,180',
    xp: '420k',
    tag: 'Jat clan',
    region: 'Family · Lineage'
  }, {
    name: 'Gill',
    leader: '@gill_s',
    members: '1,340',
    xp: '470k',
    tag: 'Jat clan',
    region: 'Family · Lineage'
  }, {
    name: 'Sidhu',
    leader: '@sidhu_s',
    members: '1,220',
    xp: '440k',
    tag: 'Jat clan',
    region: 'Family · Lineage'
  }, {
    name: 'Dhillon',
    leader: '@dhillon_s',
    members: '960',
    xp: '350k',
    tag: 'Jat clan',
    region: 'Family · Lineage'
  }, {
    name: 'Brar',
    leader: '@brar_s',
    members: '880',
    xp: '320k',
    tag: 'Jat clan',
    region: 'Family · Lineage'
  },
  // Historic Prestige
  {
    name: 'Sukerchakia',
    leader: '@ranjit_s',
    members: '1,520',
    xp: '480k',
    tag: 'Founder favorite',
    region: 'Gujranwala'
  }, {
    name: 'Ahluwalia',
    leader: '@jassa_singh',
    members: '1,240',
    xp: '450k',
    tag: 'Most active',
    region: 'Kapurthala'
  }, {
    name: 'Ramgarhia',
    leader: '@jassa_r',
    members: '1,180',
    xp: '430k',
    tag: 'Builders',
    region: 'Sri Hargobindpur'
  }, {
    name: 'Kanheya',
    leader: '@jai_singh',
    members: '850',
    xp: '310k',
    tag: 'New & growing',
    region: 'Sohian'
  }, {
    name: 'Nakai',
    leader: '@hira_s',
    members: '620',
    xp: '240k',
    tag: 'Rising',
    region: 'Chunian'
  }];
  // Reset step + transient state when opened.
  // When opened from an invite link (invitedBy present), skip the landing step
  // and pre-fill the referral code + selected misl so the flow feels personal.
  useEffect(() => {
    if (isOpen) {
      if (invitedBy) {
        setStep('auth');
        if (inviteCode) setReferralCode(inviteCode);
        if (invitedMisl) setSelectedMisl(invitedMisl);
      } else {
        setStep('landing');
        setSelectedMisl(null);
      }
      setPacketState('sealed');
      setMislView('choose');
    }
  }, [isOpen, invitedBy, invitedMisl, inviteCode]);
  // Always reset the packet animation whenever the user lands on the packet step,
  // so the sealed → opening → revealed sequence plays every time (forward or back).
  useEffect(() => {
    if (step === 'packet') setPacketState('sealed');
  }, [step]);
  const stepOrder: OnboardingStep[] = ['landing', 'auth', 'identity', 'referral', 'language', 'reward', 'packet', 'explain', 'misl', 'share', 'complete'];
  const currentIndex = stepOrder.indexOf(step);
  const nextStep = () => {
    if (currentIndex < stepOrder.length - 1) {
      setStep(stepOrder[currentIndex + 1]);
    } else {
      onComplete();
    }
  };
  const prevStep = () => {
    if (currentIndex > 0) {
      // Reset packet state if going back to packet step
      if (stepOrder[currentIndex - 1] === 'packet') setPacketState('sealed');
      // Reset misl sub-view if going back to or away from misl step
      if (stepOrder[currentIndex - 1] === 'misl' || step === 'misl') {
        setMislView('choose');
        setSelectedMisl(null);
      }
      setStep(stepOrder[currentIndex - 1]);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-3084" data-mp="3um2hc7h40xuf">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="absolute inset-0 bg-[#0B0F19]/95 backdrop-blur-md" onClick={onClose} data-id="element-3085" data-mp="3qi4hfujynj0b" />

      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-xl bg-[#131B2B] border border-[#D4AF37]/30 shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden" data-id="element-3086" data-mp="25ztyo32dnuz5">
        {/* Progress Bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-[#0B0F19]" data-id="element-3087" data-mp="1ma9vb8527eyi">
          <motion.div className="h-full bg-[#D4AF37]" initial={{
          width: '0%'
        }} animate={{
          width: `${['landing', 'auth', 'identity', 'referral', 'language', 'reward', 'packet', 'explain', 'misl', 'share', 'complete'].indexOf(step) / 10 * 100}%`
        }} data-id="element-3088" data-mp="1rae9grihvxlk" />
        </div>

        {/* Back button — hidden on first step */}
        {step !== 'landing' && step !== 'complete' && <button type="button" onClick={prevStep} className="absolute top-4 left-4 flex items-center gap-1.5 text-[#FDFBF7]/40 hover:text-[#D4AF37] transition-colors z-10 text-[10px] uppercase tracking-[0.25em] font-medium" aria-label="Go back" data-id="element-3089" data-mp="3o654qcgz74ov">
            <ChevronLeft className="w-4 h-4" data-id="element-3090" data-mp="fwdct5mrrru3" />
            Back
          </button>}

        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#FDFBF7]/40 hover:text-[#FDFBF7] transition-colors z-10" aria-label="Close" data-id="element-3091" data-mp="axsdd8fcdc6p">
          <X className="w-5 h-5" data-id="element-3092" data-mp="kowr64ta6qzy" />
        </button>

        {/* Invite banner — shown when modal is opened from an invite link */}
        {invitedBy && step !== 'complete' && <div className="bg-[#D4AF37]/10 border-b border-[#D4AF37]/30 px-4 sm:px-6 pt-9 sm:pt-10 pb-3 flex items-center gap-3" data-id="element-3093" data-mp="tq4cznem1zd5">
            <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0" data-id="element-3094" data-mp="1dbo6zog6zpjh">
              <UserPlus className="w-4 h-4 text-[#D4AF37]" data-id="element-3095" data-mp="14apxwrqwcru1" />
            </div>
            <div className="text-left" data-id="element-3096" data-mp="18brz3e4djdyn">
              <p className="text-[#D4AF37] text-[9px] uppercase tracking-[0.3em] font-bold" data-id="element-3097" data-mp="18aqo19ncyrgx">
                Invited by {invitedBy}
              </p>
              {invitedMisl && <p className="text-[#FDFBF7]/70 text-[11px] leading-tight" data-id="element-3098" data-mp="zblrg8vcx87e">
                  You'll join their misl —{' '}
                  <span className="text-[#D4AF37] font-medium" data-id="element-3099" data-mp="knih6naa7acs">
                    {invitedMisl}
                  </span>
                </p>}
            </div>
          </div>}

        <div className="p-8 sm:p-12" data-id="element-3100" data-mp="bmu3xusppe0y">
          <AnimatePresence mode="wait" data-id="element-3101" data-mp="bnvfs1hjy4jq">
            {/* Step 1: Landing Hook */}
            {step === 'landing' && <motion.div key="landing" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-8" data-id="element-3102" data-mp="zciduy8hhk1c">
                <div className="space-y-4 text-center" data-id="element-3103" data-mp="1izpxpbkebhmt">
                  <div className="inline-flex items-center justify-center w-16 h-16 border border-[#D4AF37] bg-[#0B0F19] mb-4" data-id="element-3104" data-mp="1j2cd1xdj31fl">
                    <Crown className="w-8 h-8 text-[#D4AF37]" data-id="element-3105" data-mp="19aoelcik0k4m" />
                  </div>
                  <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7]" data-id="element-3106" data-mp="1e9j9vdxpk15m">
                    Join Treasures of the Sikh Empire
                  </h2>
                  <p className="text-[#FDFBF7]/60 text-sm leading-relaxed max-w-md mx-auto" data-id="element-3107" data-mp="21xizubn73nj5">
                    Open your daily Court Packet. Build your treasury. Rise in
                    the Lahore Court. Unlock real privileges at the live
                    experience.
                  </p>
                </div>

                <div className="flex flex-col gap-3" data-id="element-3108" data-mp="2c0y0cocmlvzf">
                  <button onClick={nextStep} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2" data-id="element-3109" data-mp="2ptdwd5740tqu">
                    Join the Treasury <ArrowRight className="w-4 h-4" data-id="element-3110" data-mp="1t4y2wy0vq08g" />
                  </button>
                  <button onClick={nextStep} className="w-full py-4 border border-[#D4AF37]/30 text-[#FDFBF7] text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#D4AF37]/10 transition-colors" data-id="element-3111" data-mp="274h1p07svz83">
                    I Have an Invite
                  </button>
                </div>
              </motion.div>}

            {/* Step 2: Fast Account Creation */}
            {step === 'auth' && <motion.div key="auth" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-8" data-id="element-3112" data-mp="3ln23197zey51">
                <div className="space-y-2" data-id="element-3113" data-mp="31uvs4enrw2m6">
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-3114" data-mp="36wusdfo7ujew">
                    Create Your Account
                  </h2>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-3115" data-mp="3btl5pv8mz0wm">
                    One tap to get in. Keep it light.
                  </p>
                </div>

                <div className="space-y-3" data-id="element-3116" data-mp="2j70fiy3wcuun">
                  {/* Google — primary */}
                  <button type="button" onClick={nextStep} className="w-full py-4 bg-[#FDFBF7] text-[#0B0F19] text-sm font-medium hover:bg-white transition-colors flex items-center justify-center gap-3 shadow-lg" data-id="element-3117" data-mp="2o4s6npwpwcxs">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true" data-id="element-3118" data-mp="26n3j0uaa8zgn">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" data-id="element-3119" data-mp="21qw96qern3tf" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" data-id="element-3120" data-mp="1nwc4lebfuive" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" data-id="element-3121" data-mp="1e0s8aqsypi64" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" data-id="element-3122" data-mp="23nctnzsp7hmk" />
                    </svg>
                    Continue with Google
                  </button>

                  {/* Apple — primary */}
                  <button type="button" onClick={nextStep} className="w-full py-4 bg-[#0B0F19] text-[#FDFBF7] text-sm font-medium hover:bg-[#1a2238] transition-colors flex items-center justify-center gap-3 border border-[#FDFBF7]/20" data-id="element-3123" data-mp="1kx4uttw17dc6">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" data-id="element-3124" data-mp="31alhi1g692rq">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" data-id="element-3125" data-mp="tpwn5pa5dzyl" />
                    </svg>
                    Continue with Apple
                  </button>
                </div>

                {/* Divider */}
                <div className="relative py-2" data-id="element-3126" data-mp="18d7v7h74f1ty">
                  <div className="absolute inset-0 flex items-center" data-id="element-3127" data-mp="1mdsesfkv11oi">
                    <div className="w-full border-t border-[#D4AF37]/10" data-id="element-3128" data-mp="1mczffzo8dege"></div>
                  </div>
                  <div className="relative flex justify-center text-[10px] uppercase tracking-widest text-[#FDFBF7]/30 bg-[#131B2B] px-3" data-id="element-3129" data-mp="1hfh2ly73ef34">
                    Or use email
                  </div>
                </div>

                {/* Email fallback */}
                <div className="space-y-4" data-id="element-3130" data-mp="1n3pjlhl5sotw">
                  <div className="space-y-2" data-id="element-3131" data-mp="1n50m7e2vbqjf">
                    <label className="text-[10px] uppercase tracking-widest text-[#D4AF37]/60 font-bold" data-id="element-3132" data-mp="1s1hbwcppucym">
                      First Name
                    </label>
                    <input type="text" placeholder="Harnoor" className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] transition-colors" data-id="element-3133" data-mp="1x3gcxfxzgxse" />
                  </div>
                  <div className="space-y-2" data-id="element-3134" data-mp="2frtrm9r7ddzr">
                    <label className="text-[10px] uppercase tracking-widest text-[#D4AF37]/60 font-bold" data-id="element-3135" data-mp="2fs3idzk2np6i">
                      Email or Mobile
                    </label>
                    <div className="relative" data-id="element-3136" data-mp="2ot1cnq4pkibw">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]/40" data-id="element-3137" data-mp="2oq6slzplwzj0" />
                      <input type="text" value={email} onChange={e => setEmail(e.target.value)} placeholder="harnoor@example.com" className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 pl-12 pr-4 py-3 text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] transition-colors" data-id="element-3138" data-mp="2tp04jnp8wih2" />
                    </div>
                  </div>

                  <button type="button" onClick={nextStep} className="w-full py-3 border border-[#D4AF37]/40 text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#D4AF37]/5 transition-colors flex items-center justify-center gap-2" data-id="element-3139" data-mp="10tpgubvexizm">
                    Send Magic Link <ArrowRight className="w-4 h-4" data-id="element-3140" data-mp="1ki82ijh7me9k" />
                  </button>
                </div>
              </motion.div>}

            {/* Step 3: Identity Moment */}
            {step === 'identity' && <motion.div key="identity" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-8" data-id="element-3141" data-mp="o35cp5i4cl7i">
                <div className="space-y-2" data-id="element-3142" data-mp="16vzib8tur0z2">
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-3143" data-mp="1btr91zirgh1o">
                    Your Path to the Court
                  </h2>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-3144" data-mp="xsyczact9dwg">
                    How are you entering the Treasury?
                  </p>
                </div>

                <div className="grid gap-3" data-id="element-3145" data-mp="3ik8llas08ely">
                  {[{
                id: 'new',
                label: "I'm new to the treasury",
                icon: Sparkles
              }, {
                id: 'invited',
                label: 'I was invited by a friend',
                icon: UserPlus
              }].map(option => <button key={option.id} onClick={nextStep} className="group flex items-center gap-4 p-4 border border-[#D4AF37]/20 bg-[#0B0F19]/40 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all text-left" data-id="element-3146" data-mp="3oz5z62mwsvhs">
                      <div className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center group-hover:border-[#D4AF37] transition-colors" data-id="element-3147" data-mp="2re9z4urvkexl">
                        <option.icon className="w-5 h-5 text-[#D4AF37]/60 group-hover:text-[#D4AF37]" data-id="element-3148" data-mp="31ad5kctrezqc" />
                      </div>
                      <span className="text-[#FDFBF7] text-sm font-medium" data-id="element-3149" data-mp="2djr6hb1qgpsh">
                        {option.label}
                      </span>
                      <ChevronRight className="ml-auto w-4 h-4 text-[#D4AF37]/40 group-hover:text-[#D4AF37]" data-id="element-3150" data-mp="3qtctb6nuyf18" />
                    </button>)}
                </div>
              </motion.div>}

            {/* Step 4: Invite Capture */}
            {step === 'referral' && <motion.div key="referral" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-8" data-id="element-3151" data-mp="3uuya333ijdgp">
                <div className="space-y-2" data-id="element-3152" data-mp="id05e51i5xke">
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-3153" data-mp="ie1jkidtf06c">
                    Enter Referral Code
                  </h2>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-3154" data-mp="2k4wfda80uzd2">
                    If you were invited, enter your code to unlock a bonus
                    packet.
                  </p>
                </div>

                <div className="space-y-6" data-id="element-3155" data-mp="2e2bc4hhxbi2c">
                  <input type="text" value={referralCode} onChange={e => setReferralCode(e.target.value.toUpperCase())} placeholder="LAHORE-2024" className="w-full bg-[#0B0F19] border border-[#D4AF37]/20 px-6 py-4 text-[#FDFBF7] text-center text-xl tracking-[0.2em] font-mono focus:outline-none focus:border-[#D4AF37] transition-colors" data-id="element-3156" data-mp="2e19q5hzddb5u" />

                  <div className="flex gap-3" data-id="element-3157" data-mp="1hdv9bjpszc7a">
                    <button onClick={nextStep} className="flex-1 py-4 border border-[#D4AF37]/30 text-[#FDFBF7]/60 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#D4AF37]/5 transition-colors" data-id="element-3158" data-mp="1mbdhh7vzxne8">
                      Skip
                    </button>
                    <button onClick={nextStep} className="flex-[2] py-4 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2" data-id="element-3159" data-mp="3eazvcx6cz3lq">
                      Apply Code <Check className="w-4 h-4" data-id="element-3160" data-mp="30brvs36a9th0" />
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Step 5: Language Selection */}
            {step === 'language' && <motion.div key="language" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-8" data-id="element-3161" data-mp="3t1qme304lyns">
                <div className="space-y-2" data-id="element-3162" data-mp="frax7sr3l8qc">
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-3163" data-mp="n5bpjh69i2cb">
                    Choose Your Language
                  </h2>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-3164" data-mp="s5fwogla5kpv">
                    Select how you'd like to experience the Treasury.
                  </p>
                </div>

                <div className="grid gap-3" data-id="element-3165" data-mp="122asusu2z90z">
                  {[{
                id: 'en',
                label: 'English',
                sub: 'Standard experience'
              }, {
                id: 'pa',
                label: 'ਪੰਜਾਬੀ (Punjabi)',
                sub: 'Native experience'
              }, {
                id: 'both',
                label: 'Both / ਦੋਵੇਂ',
                sub: 'Bilingual experience'
              }].map(opt => <button key={opt.id} onClick={() => {
                setLanguage(opt.id as any);
                nextStep();
              }} className={`flex items-center gap-4 p-5 border transition-all text-left ${language === opt.id ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#D4AF37]/20 bg-[#0B0F19]/40 hover:border-[#D4AF37]/50'}`} data-id="element-3166" data-mp="1tsysu1k0q8d2">
                      <Globe className={`w-5 h-5 ${language === opt.id ? 'text-[#D4AF37]' : 'text-[#D4AF37]/40'}`} data-id="element-3167" data-mp="28hxxfgp44zq7" />
                      <div data-id="element-3168" data-mp="23lz9tvblzlqu">
                        <p className="text-[#FDFBF7] text-sm font-medium" data-id="element-3169" data-mp="2hjxgeejoudvs">
                          {opt.label}
                        </p>
                        <p className="text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest mt-0.5" data-id="element-3170" data-mp="2cizwnxj15bs6">
                          {opt.sub}
                        </p>
                      </div>
                      {language === opt.id && <Check className="ml-auto w-4 h-4 text-[#D4AF37]" data-id="element-3171" data-mp="bchox4yfw61" />}
                    </button>)}
                </div>
              </motion.div>}

            {/* Step 6: Welcome Reward */}
            {step === 'reward' && <motion.div key="reward" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 1.1
          }} className="space-y-8 text-center" data-id="element-3172" data-mp="3rhe05qr91s1p">
                <div className="space-y-2" data-id="element-3173" data-mp="e9u2on3h6uk9">
                  <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.3em] font-bold" data-id="element-3174" data-mp="e8st6n23yg4n">
                    Welcome to the Treasures of the Sikh Empire
                  </p>
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-3175" data-mp="16shhxlvsjfdf">
                    Your Collection Awaits
                  </h2>
                </div>

                <div className="relative py-2" data-id="element-3176" data-mp="1bnslfpxavavf">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.25),_transparent_65%)] blur-3xl" data-id="element-3177" data-mp="1blzcu534mgks" />
                  <motion.div animate={{
                y: [0, -6, 0]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="relative w-52 sm:w-60 mx-auto" data-id="element-3178" data-mp="12nx7jdcd11rp">
                    <div className="absolute -inset-2 bg-gradient-to-b from-[#D4AF37]/40 via-transparent to-[#D4AF37]/40 blur-xl" data-id="element-3179" data-mp="izeo7ce1cikx" />
                    <img src="https://cdn.magicpatterns.com/uploads/3hBK2oMvmeZZy5P54xmeKQ/Lahore_Court_Collection.png" alt="Lahore Court Collection — Founders' Edition" className="relative w-full drop-shadow-[0_20px_50px_rgba(0,0,0,0.8)]" data-id="element-3180" data-mp="ey2uovs1jd7g" />
                    <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.4
                }} className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#0B0F19] px-4 py-1.5 text-[9px] uppercase tracking-[0.25em] font-bold whitespace-nowrap shadow-lg" data-id="element-3181" data-mp="2qjlishy6up1e">
                      Founders' Edition · Unlocked
                    </motion.div>
                  </motion.div>
                </div>

                <p className="text-[#FDFBF7]/70 text-sm max-w-sm mx-auto leading-relaxed pt-6" data-id="element-3182" data-mp="1j5qphewpptr9">
                  You have unlocked the{' '}
                  <span className="text-[#D4AF37] font-bold" data-id="element-3183" data-mp="hketdsv01hgc">
                    Lahore Court Collection
                  </span>
                  . Eight founding faces of sovereignty are waiting to be
                  claimed — starting with your first packet.
                </p>

                <button type="button" onClick={nextStep} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(212,175,55,0.3)] group" data-id="element-3184" data-mp="mhx2bj90nwoc">
                  Claim Your First Packet
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-3185" data-mp="1596c7tmp8ngg" />
                </button>
              </motion.div>}

            {/* Step 7: First Packet Opening */}
            {step === 'packet' && <motion.div key="packet" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="text-center min-h-[500px] flex flex-col items-center justify-center relative" data-id="element-3186" data-mp="1ebfpgr9cquxj">
                <AnimatePresence mode="wait" data-id="element-3187" data-mp="1sui6zl7fnfhl">
                  {/* ============ SEALED ============ */}
                  {packetState === 'sealed' && <motion.div key="sealed" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} transition={{
                duration: 0.5
              }} className="flex flex-col items-center text-center space-y-7 w-full" data-id="element-3188" data-mp="1jvwrjyx9cgl1">
                      <div className="space-y-2" data-id="element-3189" data-mp="3oc6h64qojpzs">
                        <p className="text-[#8B0000] text-[10px] uppercase tracking-[0.3em] font-bold" data-id="element-3190" data-mp="3t859g3f4ps5h">
                          First Reward
                        </p>
                        <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-3191" data-mp="728az9syb94s">
                          The Darbar Awaits
                        </h2>
                      </div>

                      <motion.div animate={{
                  y: [0, -8, 0]
                }} transition={{
                  duration: 3.5,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="relative w-56 sm:w-64" data-id="element-3192" data-mp="b17dyov4gvtl">
                        <motion.div animate={{
                    opacity: [0.4, 0.7, 0.4]
                  }} transition={{
                    duration: 2.8,
                    repeat: Infinity
                  }} className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.4),_transparent_65%)] blur-2xl pointer-events-none" data-id="element-3193" data-mp="2f9lmb46piaf8" />
                        <motion.button type="button" onClick={() => {
                    setPacketState('opening');
                    setTimeout(() => setPacketState('revealed'), 1900);
                  }} whileHover={{
                    scale: 1.04
                  }} whileTap={{
                    scale: 0.96
                  }} aria-label="Break the seal and open your first packet" className="relative block w-full cursor-pointer" data-id="element-3194" data-mp="2t19wv31b4qu3">
                          <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Sealed envelope" className="w-full drop-shadow-[0_25px_50px_rgba(0,0,0,0.7)]" data-id="element-3195" data-mp="1f381oo8il15y" />
                          <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-[#D4AF37]/60 text-[9px] tracking-[0.3em] uppercase whitespace-nowrap" data-id="element-3196" data-mp="vdft8nlbek4o">
                            Click to open
                          </span>
                        </motion.button>
                      </motion.div>

                      <p className="text-[#FDFBF7]/60 text-sm font-light max-w-sm mx-auto leading-relaxed" data-id="element-3197" data-mp="2brxmambj07vm">
                        A sealed envelope from the Maharaja's court awaits.
                        Break the wax — discover the artifact within.
                      </p>

                      <motion.button type="button" whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={() => {
                  setPacketState('opening');
                  setTimeout(() => setPacketState('revealed'), 1900);
                }} className="px-10 py-4 bg-[#8B0000] hover:bg-[#A00000] text-[#FDFBF7] text-xs tracking-[0.3em] uppercase font-medium shadow-2xl transition-colors flex items-center gap-3" data-id="element-3198" data-mp="512rdcbbueml">
                        <Sparkles className="w-4 h-4" data-id="element-3199" data-mp="sr5f5g87kyfe" /> Break the Seal
                      </motion.button>
                    </motion.div>}

                  {/* ============ OPENING ============ */}
                  {packetState === 'opening' && <motion.div key="opening" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="relative flex items-center justify-center w-full min-h-[440px]" data-id="element-3200" data-mp="1ln67m25fedaz">
                      {/* Expanding gold burst */}
                      <motion.div initial={{
                  scale: 0.4,
                  opacity: 0
                }} animate={{
                  scale: 4,
                  opacity: [0, 0.9, 0]
                }} transition={{
                  duration: 1.8,
                  ease: 'easeOut'
                }} className="absolute w-80 h-80 rounded-full bg-[radial-gradient(circle,_rgba(212,175,55,0.6),_transparent_60%)] blur-3xl" data-id="element-3201" data-mp="17twy81hh9rsg" />

                      {/* Shaking + shrinking envelope */}
                      <motion.div initial={{
                  scale: 1,
                  opacity: 1
                }} animate={{
                  scale: [1, 1.1, 0.6],
                  opacity: [1, 1, 0],
                  rotateZ: [0, -3, 4, -2, 0]
                }} transition={{
                  duration: 1.8,
                  times: [0, 0.5, 1],
                  ease: 'easeInOut'
                }} className="relative w-56 sm:w-64 z-10" data-id="element-3202" data-mp="7pxn2zeppxa9">
                        <img src="https://cdn.magicpatterns.com/uploads/9o4p7UEYp4VE7S65Drh7mS/envelope.png" alt="Opening envelope" className="w-full drop-shadow-[0_0_80px_rgba(212,175,55,0.7)]" data-id="element-3203" data-mp="366ru5tggf939" />
                      </motion.div>

                      {/* Radial sparkle particles */}
                      {Array.from({
                  length: 20
                }).map((_, i) => {
                  const angle = i / 20 * Math.PI * 2;
                  const distance = 220;
                  return <motion.div key={i} initial={{
                    x: 0,
                    y: 0,
                    opacity: 0,
                    scale: 0
                  }} animate={{
                    x: Math.cos(angle) * distance,
                    y: Math.sin(angle) * distance,
                    opacity: [0, 1, 0],
                    scale: [0, 1.2, 0]
                  }} transition={{
                    duration: 1.6,
                    delay: 0.2,
                    ease: 'easeOut'
                  }} className="absolute w-1.5 h-1.5 bg-[#D4AF37] rounded-full shadow-[0_0_10px_rgba(212,175,55,1)]" data-id="element-3204" data-mp="3k99wazgye1mc" data-mpi={i} />;
                })}
                    </motion.div>}

                  {/* ============ REVEALED ============ */}
                  {packetState === 'revealed' && <motion.div key="revealed" initial={{
                opacity: 0,
                scale: 0.8
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.6
              }} className="space-y-8 w-full" data-id="element-3205" data-mp="1dhlmu2bk0ekm">
                      <div className="space-y-2" data-id="element-3206" data-mp="w89nkhr83i1g">
                        <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.3em] font-bold" data-id="element-3207" data-mp="110awbtp6mqmq">
                          Premium Reveal
                        </p>
                        <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-3208" data-mp="154ilfrzjy8lh">
                          Ranjit Singh
                        </h2>
                      </div>

                      <motion.div initial={{
                  rotateY: 180,
                  opacity: 0
                }} animate={{
                  rotateY: 0,
                  opacity: 1
                }} transition={{
                  duration: 0.8,
                  delay: 0.2,
                  ease: 'easeOut'
                }} className="relative w-52 aspect-[3/4] mx-auto border-2 border-[#D4AF37] shadow-[0_0_50px_rgba(212,175,55,0.5)] overflow-hidden" data-id="element-3209" data-mp="1f0veqor6qkfp">
                        <img src="https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png" alt="Ranjit Singh" className="w-full h-full object-cover" data-id="element-3210" data-mp="1p5bs9bh6zpgc" />
                        <div className="absolute top-2 left-2 px-2 py-1 bg-[#D4AF37] text-[#0B0F19] text-[8px] uppercase tracking-widest font-bold" data-id="element-3211" data-mp="2ctl9s17dhr1o">
                          Founders' Edition
                        </div>
                      </motion.div>

                      <div className="space-y-6" data-id="element-3212" data-mp="11fznqowt85kd">
                        <p className="text-[#FDFBF7]/60 text-sm leading-relaxed max-w-xs mx-auto" data-id="element-3213" data-mp="wf24sa3z77ht">
                          You have received{' '}
                          <span className="text-[#D4AF37] font-bold" data-id="element-3214" data-mp="1yqflllxz64nv">
                            The Lion of Punjab
                          </span>
                          . This is a rare starter collectible to begin your
                          treasury.
                        </p>
                        <button type="button" onClick={nextStep} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2 group" data-id="element-3215" data-mp="1jtewzrb2yptm">
                          Continue
                          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-3216" data-mp="114rp711irmcc" />
                        </button>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>}

            {/* Step 8: Quick Explain */}
            {step === 'explain' && <motion.div key="explain" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-10" data-id="element-3217" data-mp="37r643wkqt0fy">
                <div className="space-y-2 text-center" data-id="element-3218" data-mp="2p0fza2bm33kg">
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-3219" data-mp="2oyv54dt7lmn4">
                    How it Works
                  </h2>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-3220" data-mp="2yrj3hczu7yny">
                    The path to sovereignty is simple.
                  </p>
                </div>

                <div className="space-y-6" data-id="element-3221" data-mp="219j25voh6at9">
                  {[{
                icon: Gift,
                title: 'Daily Packets',
                desc: 'Come back daily to open a new packet and grow your collection.'
              }, {
                icon: Repeat,
                title: 'Complete Sets',
                desc: 'Trade duplicates with others to complete historical sets.'
              }, {
                icon: Crown,
                title: 'Rise in Rank',
                desc: 'Unlock real-world rewards and privileges as you gain XP.'
              }].map((item, i) => <div key={i} className="flex gap-5" data-id="element-3222" data-mp="1p9m0kaq7siv9" data-mpi={i}>
                      <div className="w-12 h-12 shrink-0 border border-[#D4AF37]/30 bg-[#0B0F19] flex items-center justify-center" data-id="element-3223" data-mp="1p6zn5ugn7954">
                        <item.icon className="w-5 h-5 text-[#D4AF37]" data-id="element-3224" data-mp="30npwhmgd4h2y" />
                      </div>
                      <div className="space-y-1" data-id="element-3225" data-mp="2vq7ktoaynnr9">
                        <h4 className="text-[#FDFBF7] text-sm font-bold uppercase tracking-wider" data-id="element-3226" data-mp="2vrilvgd0uheo">
                          {item.title}
                        </h4>
                        <p className="text-[#FDFBF7]/50 text-xs leading-relaxed" data-id="element-3227" data-mp="mhjahq904yvm">
                          {item.desc}
                        </p>
                      </div>
                    </div>)}
                </div>

                <button onClick={nextStep} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2" data-id="element-3228" data-mp="dhwi1ty933ep">
                  Got It <ArrowRight className="w-4 h-4" data-id="element-3229" data-mp="rfktkb6fgy73" />
                </button>
              </motion.div>}

            {/* Step 9: Misl Path */}
            {step === 'misl' && <motion.div key="misl" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-6" data-id="element-3230" data-mp="1k2xhaoobrc1z">
                <AnimatePresence mode="wait" data-id="element-3231" data-mp="3nedghwukd9bs">
                  {/* ============ CHOOSE ============ */}
                  {mislView === 'choose' && <motion.div key="choose" initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} className="space-y-6" data-id="element-3232" data-mp="j0f3mt5mynbo">
                      <div className="space-y-2" data-id="element-3233" data-mp="xww953r597qb">
                        <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-3234" data-mp="xv35zyfqp5n2">
                          Join Your Misl{' '}
                          <span className="text-[#FDFBF7]/40" data-id="element-3235" data-mp="1a75gm5ki9ujg">(Clan)</span>
                        </h2>
                        <p className="text-[#FDFBF7]/60 text-sm" data-id="element-3236" data-mp="1f6qfyh3l84wp">
                          The Sikh Confederacy was built on Misls — clans and
                          sovereign states of the Khalsa. Join one to grow
                          faster.
                        </p>
                      </div>

                      <div className="grid gap-3" data-id="element-3237" data-mp="1t5q31kbgcbqr">
                        <button type="button" onClick={() => {
                    setSelectedMisl('Ahluwalia');
                    setMislView('confirm-invite');
                  }} className="group flex items-center gap-4 p-5 border border-[#D4AF37] bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 transition-all text-left" data-id="element-3238" data-mp="1t4ez9ki0b3zn">
                          <div className="w-10 h-10 border border-[#D4AF37] flex items-center justify-center" data-id="element-3239" data-mp="btzznz63iaoq">
                            <Check className="w-5 h-5 text-[#D4AF37]" data-id="element-3240" data-mp="7tpkbvwey7x7" />
                          </div>
                          <div className="flex-1" data-id="element-3241" data-mp="llcpymc8wssn">
                            <p className="text-[#FDFBF7] text-sm font-bold" data-id="element-3242" data-mp="lm5qv6oiwo2v">
                              Join with my invite
                            </p>
                            <p className="text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest mt-0.5" data-id="element-3243" data-mp="vh5ylumug255">
                              Use your referral link
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#D4AF37]/60" data-id="element-3244" data-mp="14iduy75suswl" />
                        </button>
                        <button type="button" onClick={() => setMislView('browse')} className="group flex items-center gap-4 p-5 border border-[#D4AF37]/20 bg-[#0B0F19]/40 hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5 transition-all text-left" data-id="element-3245" data-mp="zi9g0lkji68h">
                          <div className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center group-hover:border-[#D4AF37]/60 transition-colors" data-id="element-3246" data-mp="1lrg506cwtmb4">
                            <Users className="w-5 h-5 text-[#D4AF37]/60 group-hover:text-[#D4AF37] transition-colors" data-id="element-3247" data-mp="1c2ofs5z8hg3w" />
                          </div>
                          <div className="flex-1" data-id="element-3248" data-mp="y1c3c4ja7wmr">
                            <p className="text-[#FDFBF7] text-sm font-bold" data-id="element-3249" data-mp="131qehvg6jmp0">
                              Browse active clans (Misls)
                            </p>
                            <p className="text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest mt-0.5" data-id="element-3250" data-mp="p4bk0cb2koq6">
                              Find your community
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#D4AF37]/40 group-hover:text-[#D4AF37]/80 transition-colors" data-id="element-3251" data-mp="2nhv09iz8f4oc" />
                        </button>
                      </div>
                    </motion.div>}

                  {/* ============ BROWSE ============ */}
                  {mislView === 'browse' && <motion.div key="browse" initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} className="space-y-5" data-id="element-3252" data-mp="11hdou1bdrhru">
                      <button type="button" onClick={() => setMislView('choose')} className="flex items-center gap-1.5 text-[#FDFBF7]/50 hover:text-[#D4AF37] text-[10px] uppercase tracking-[0.25em] font-medium transition-colors" data-id="element-3253" data-mp="dm15bsgl6izc">
                        <ChevronLeft className="w-3 h-3" data-id="element-3254" data-mp="2gdonw5hpa2d4" /> Back to options
                      </button>

                      <div className="space-y-2" data-id="element-3255" data-mp="2kf06hr25pnbt">
                        <h2 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7]" data-id="element-3256" data-mp="2p94dubfc3xdf">
                          Choose Your Clan{' '}
                          <span className="text-[#FDFBF7]/40 text-xl" data-id="element-3257" data-mp="2u9aa64wju81u">
                            (Misl)
                          </span>
                        </h2>
                        <p className="text-[#FDFBF7]/60 text-sm" data-id="element-3258" data-mp="21jjwty2qra6s">
                          Pick a sovereign state of the Khalsa to join. You can
                          switch later.
                        </p>
                      </div>

                      {/* Leadership rule callout */}
                      <div className="flex items-start gap-3 p-3.5 border border-[#D4AF37]/30 bg-[#D4AF37]/5" data-id="element-3259" data-mp="3hvp75hfdejjm">
                        <Crown className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" data-id="element-3260" data-mp="3muk19fisfuj1" />
                        <p className="text-[#FDFBF7]/80 text-xs leading-relaxed" data-id="element-3261" data-mp="1s7fd2nxmwxs2">
                          The member with the most{' '}
                          <span className="text-[#D4AF37] font-bold" data-id="element-3262" data-mp="1wa26gt9yeqrd">XP</span>{' '}
                          in each clan becomes its{' '}
                          <span className="text-[#D4AF37] font-bold" data-id="element-3263" data-mp="1nf4afdbiexvt">
                            Misldar
                          </span>{' '}
                          (Clan Chief). Earn the most to lead.
                        </p>
                      </div>

                      <div className="grid gap-2.5 max-h-[400px] overflow-y-auto pr-1" data-id="element-3264" data-mp="1dfdgpdurwmws">
                        {misls.map(m => {
                    const isSelected = selectedMisl === m.name;
                    return <button key={m.name} type="button" onClick={() => setSelectedMisl(m.name)} className={`group relative flex items-stretch gap-4 p-4 border transition-all text-left ${isSelected ? 'border-[#D4AF37] bg-[#D4AF37]/10 shadow-[0_0_20px_rgba(212,175,55,0.15)]' : 'border-[#D4AF37]/20 bg-[#0B0F19]/40 hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5'}`} data-id="element-3265" data-mp="2tv6jdt142cmr">
                              {/* Crest */}
                              <div className={`w-12 h-12 shrink-0 border flex items-center justify-center transition-colors ${isSelected ? 'border-[#D4AF37] bg-[#D4AF37]/15' : 'border-[#D4AF37]/30 group-hover:border-[#D4AF37]/60'}`} data-id="element-3266" data-mp="3cyi680pgl7ux">
                                <Shield className={`w-5 h-5 ${isSelected ? 'text-[#D4AF37]' : 'text-[#D4AF37]/60'}`} data-id="element-3267" data-mp="4i6w6wzunw9n" />
                              </div>

                              {/* Info */}
                              <div className="flex-1 min-w-0" data-id="element-3268" data-mp="22bvq79jqc0s6">
                                <div className="flex items-center gap-2 flex-wrap" data-id="element-3269" data-mp="5a6n47y6kseh">
                                  <p className="text-[#FDFBF7] text-sm font-bold" data-id="element-3270" data-mp="a8hyin44jayt">
                                    {m.name}
                                  </p>
                                  <span className="text-[#D4AF37]/60 text-[8px] uppercase tracking-[0.25em] border border-[#D4AF37]/25 px-1.5 py-0.5" data-id="element-3271" data-mp="f6jjjdl41kcx">
                                    {m.tag}
                                  </span>
                                </div>
                                <p className="text-[#FDFBF7]/40 text-[10px] font-mono mt-0.5" data-id="element-3272" data-mp="t6b11oau5ivo">
                                  Led by {m.leader} · {m.region}
                                </p>
                                <div className="flex items-center gap-3 mt-2 text-[9px] uppercase tracking-widest text-[#FDFBF7]/50" data-id="element-3273" data-mp="12wdutqps70v2">
                                  <span className="flex items-center gap-1" data-id="element-3274" data-mp="17ufcc77k6s4f">
                                    <Users className="w-2.5 h-2.5 text-[#D4AF37]/50" data-id="element-3275" data-mp="1byn0a267041l" />
                                    {m.members} members
                                  </span>
                                  <span className="text-[#D4AF37]/20" data-id="element-3276" data-mp="2sdhsnf2aunyi">·</span>
                                  <span className="flex items-center gap-1" data-id="element-3277" data-mp="2ney2f2goaq0c">
                                    <Sparkles className="w-2.5 h-2.5 text-[#D4AF37]/50" data-id="element-3278" data-mp="2igd2z598wacy" />
                                    {m.xp} XP
                                  </span>
                                </div>
                              </div>

                              {isSelected && <Check className="absolute top-3 right-3 w-4 h-4 text-[#D4AF37]" data-id="element-3279" data-mp="36ebypr1vkcu0" />}
                            </button>;
                  })}
                      </div>

                      <button type="button" onClick={() => selectedMisl && nextStep()} disabled={!selectedMisl} className={`w-full py-4 text-xs uppercase tracking-[0.2em] font-bold transition-colors flex items-center justify-center gap-2 group ${selectedMisl ? 'bg-[#D4AF37] text-[#0B0F19] hover:bg-[#E5C158] shadow-[0_0_30px_rgba(212,175,55,0.3)]' : 'bg-[#D4AF37]/20 text-[#0B0F19]/40 cursor-not-allowed'}`} data-id="element-3280" data-mp="2q0lw80ybg1m">
                        {selectedMisl ? `Join ${selectedMisl}` : 'Select a clan'}
                        {selectedMisl && <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-3281" data-mp="65jzrjxnigpt" />}
                      </button>
                    </motion.div>}

                  {/* ============ CONFIRM INVITE ============ */}
                  {mislView === 'confirm-invite' && <motion.div key="confirm-invite" initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} className="space-y-6 text-center" data-id="element-3282" data-mp="owkvlz3h5w55">
                      <button type="button" onClick={() => setMislView('choose')} className="flex items-center gap-1.5 text-[#FDFBF7]/50 hover:text-[#D4AF37] text-[10px] uppercase tracking-[0.25em] font-medium transition-colors" data-id="element-3283" data-mp="1qp5lj90uv8h1">
                        <ChevronLeft className="w-3 h-3" data-id="element-3284" data-mp="1uo63kpbie72z" /> Back
                      </button>

                      <div className="space-y-2 pt-2" data-id="element-3285" data-mp="3b2ec3fzg2fvm">
                        <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.3em] font-bold" data-id="element-3286" data-mp="365e3oyf4h11c">
                          Welcome to the Court
                        </p>
                        <h2 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7]" data-id="element-3287" data-mp="28qjg4kh2amlh">
                          You've joined {selectedMisl}
                        </h2>
                      </div>

                      <div className="relative py-2" data-id="element-3288" data-mp="2hr96v9rgtmsc">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.2),_transparent_70%)] blur-2xl" data-id="element-3289" data-mp="2hpy1x6maa8zn" />
                        <div className="relative w-24 h-24 mx-auto border-2 border-[#D4AF37] bg-[#0B0F19] flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.3)]" data-id="element-3290" data-mp="1p2k5d9mhzfg2">
                          <Shield className="w-12 h-12 text-[#D4AF37]" data-id="element-3291" data-mp="1tzkddq2wqs9t" />
                        </div>
                      </div>

                      <p className="text-[#FDFBF7]/60 text-sm max-w-xs mx-auto leading-relaxed" data-id="element-3292" data-mp="11dj2e1wfowi6">
                        Your invite is honored. You'll find your fellow members
                        on the dashboard.
                      </p>

                      <button type="button" onClick={nextStep} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2 group shadow-[0_0_30px_rgba(212,175,55,0.3)]" data-id="element-3293" data-mp="ys1lb3shilxu">
                        Continue
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-3294" data-mp="fv0fxhv0qjtd" />
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>}

            {/* Step 10: WhatsApp Growth Prompt */}
            {step === 'share' && <motion.div key="share" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-7" data-id="element-3295" data-mp="3t93se9iwezx3">
                <div className="space-y-2 text-center" data-id="element-3296" data-mp="170waat114pjr">
                  <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.3em] font-bold" data-id="element-3297" data-mp="172gzs48hliar">
                    Final Step
                  </p>
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-3298" data-mp="x99wqq17yedu">
                    Bring Others In
                  </h2>
                  <p className="text-[#FDFBF7]/60 text-sm max-w-sm mx-auto" data-id="element-3299" data-mp="122cmso6sf5pa">
                    This is the invitation your friends and family will receive.
                    Personal, premium, and historically grounded.
                  </p>
                </div>

                {/* Plain-text invitation message */}
                {(() => {
              const origin = typeof window !== 'undefined' ? window.location.origin : 'https://lahore-treasury.app';
              const inviterName = 'Harnoor';
              const inviterCode = 'HARNOOR-123';
              const mislName = selectedMisl || 'Lahore';
              // Clean vanity URL that appears in the shared text + preview.
              const displayLink = `https://lahore-treasury.app/join/${inviterCode}`;
              // Actual working URL (drops them into onboarding pre-filled).
              // Used by the clickable anchor inside the preview for the demo.
              const actualLink = `${origin}/treasure?ref=${inviterCode}&from=${encodeURIComponent(inviterName)}&misl=${encodeURIComponent(mislName)}`;
              const intro = `You're invited to Treasures of the Sikh Empire — a premium collectible experience built around the Sikh Empire.\n\nI've just joined ${mislName}. Every artifact (Maharajas, generals, jewels, fortresses) carries real historical weight. Open daily packets, complete sets, and build a treasury that means something.\n\nJoin me here and claim your free Founders' Edition card:\n`;
              // Full message used for WhatsApp share + clipboard copy.
              const inviteMessage = intro + displayLink;
              return <>
                      <motion.div initial={{
                  opacity: 0,
                  y: 12
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.5,
                  delay: 0.1
                }} className="bg-[#0B0F19] border border-[#D4AF37]/20 p-5 sm:p-6" data-id="element-3300" data-mp="1dy5655era3lb">
                        <p className="text-[#FDFBF7]/80 text-sm leading-relaxed whitespace-pre-line text-left" data-id="element-3301" data-mp="2hyf4v8llm21">
                          {intro}
                          <a href={actualLink} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline decoration-[#D4AF37]/40 underline-offset-2 hover:decoration-[#D4AF37] break-all" data-id="element-3302" data-mp="2e3h56myb37ym">
                            {displayLink}
                          </a>
                        </p>
                      </motion.div>

                      {/* Action buttons */}
                      <div className="space-y-3" data-id="element-3303" data-mp="31siesdivkp5h">
                        <a href={`https://wa.me/?text=${encodeURIComponent(inviteMessage)}`} target="_blank" rel="noopener noreferrer" className="w-full py-4 bg-[#25D366] text-white text-xs uppercase tracking-[0.2em] font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 shadow-lg" data-id="element-3304" data-mp="2nts8c7a61d4g">
                          <MessageCircle className="w-4 h-4" data-id="element-3305" data-mp="3j61hprs27s6u" /> Share on
                          WhatsApp
                        </a>
                        <button onClick={() => {
                    navigator.clipboard.writeText(inviteMessage);
                    alert('Invitation copied!');
                  }} className="w-full py-4 border border-[#D4AF37]/30 text-[#FDFBF7] text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#D4AF37]/10 transition-colors flex items-center justify-center gap-2" data-id="element-3306" data-mp="39dm1p00bpdol">
                          <Copy className="w-4 h-4" data-id="element-3307" data-mp="2qh2x8o0gsdyd" /> Copy Invite Link
                        </button>
                        <button onClick={() => setShowSkipShareConfirm(true)} className="w-full py-4 text-[#FDFBF7]/30 text-[10px] uppercase tracking-[0.2em] font-medium hover:text-[#FDFBF7]/60 transition-colors" data-id="element-3308" data-mp="onwmumzmdrvw">
                          Skip for now
                        </button>
                      </div>

                      {/* Emotional skip-share intercept */}
                      <AnimatePresence data-id="element-3309" data-mp="2cb0ztvzi9ue1">
                        {showSkipShareConfirm && <motion.div key="skip-confirm" initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} className="absolute inset-0 z-20 flex items-center justify-center p-4 sm:p-6 bg-[#0B0F19]/85 backdrop-blur-sm" data-id="element-3310" data-mp="2ga1gb7uigkxv">
                            <motion.div initial={{
                      opacity: 0,
                      scale: 0.94,
                      y: 16
                    }} animate={{
                      opacity: 1,
                      scale: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      scale: 0.94,
                      y: 16
                    }} transition={{
                      duration: 0.35,
                      ease: 'easeOut'
                    }} className="relative w-full max-w-md bg-gradient-to-b from-[#131B2B] to-[#0B0F19] border border-[#D4AF37]/40 shadow-[0_30px_80px_rgba(0,0,0,0.7)] p-7 sm:p-8 text-center space-y-6" data-id="element-3311" data-mp="38z78qv3gn8y4">
                              {/* Decorative gold seam */}
                              <div className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/70 to-transparent" data-id="element-3312" data-mp="rqt1m5qgltsk" />

                              <div className="relative w-14 h-14 mx-auto" data-id="element-3313" data-mp="iotin7vvwfmz">
                                <motion.div animate={{
                          scale: [1, 1.15, 1],
                          opacity: [0.5, 0.9, 0.5]
                        }} transition={{
                          duration: 2.4,
                          repeat: Infinity,
                          ease: 'easeInOut'
                        }} className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.55),_transparent_70%)] blur-xl" data-id="element-3314" data-mp="iqw89qt4k6r6" />
                                <div className="relative w-full h-full border border-[#D4AF37] bg-[#0B0F19] flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.45)]" data-id="element-3315" data-mp="3r3tq2f3sapiq">
                                  <Heart className="w-6 h-6 text-[#D4AF37]" data-id="element-3316" data-mp="1rwwilvaooxdo" />
                                </div>
                              </div>

                              <div className="space-y-3" data-id="element-3317" data-mp="1ix024gyg74tp">
                                <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.3em] font-bold" data-id="element-3318" data-mp="1iykvi3910hpz">
                                  Before you go
                                </p>
                                <h3 className="font-serif text-2xl sm:text-[28px] text-[#FDFBF7] leading-tight" data-id="element-3319" data-mp="2aop99j2a66i8">
                                  Don't let this end with you
                                </h3>
                                <p className="text-[#FDFBF7]/70 text-sm leading-relaxed" data-id="element-3320" data-mp="21n7zsjjdp32i">
                                  For two hundred years, our stories have been
                                  kept alive by one thing — someone choosing to
                                  pass them on.
                                </p>
                                <p className="text-[#FDFBF7]/70 text-sm leading-relaxed" data-id="element-3321" data-mp="36uiyazvjsk7h">
                                  One share. One cousin, one friend, one elder
                                  who deserves to see this. That's how the
                                  Lahore Darbar lives again — through{' '}
                                  <span className="text-[#D4AF37] font-medium" data-id="element-3322" data-mp="3kmytxfm4dfyd">
                                    you
                                  </span>
                                  .
                                </p>
                                <p className="text-[#FDFBF7]/50 text-[11px] italic pt-1" data-id="element-3323" data-mp="3ukv15wbxrqr7">
                                  Every founding member who shares brings the
                                  court one step closer to home.
                                </p>
                              </div>

                              <div className="space-y-2.5 pt-1" data-id="element-3324" data-mp="2487c1wmlnr3b">
                                <button type="button" onClick={() => setShowSkipShareConfirm(false)} className="w-full py-3.5 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(212,175,55,0.35)]" data-id="element-3325" data-mp="1lavrqbrh8ias">
                                  <Sparkles className="w-4 h-4" data-id="element-3326" data-mp="12enrjmdbmxmc" />
                                  Yes — I'll share
                                </button>
                                <button type="button" onClick={() => {
                          setShowSkipShareConfirm(false);
                          nextStep();
                        }} className="w-full py-3 text-[#FDFBF7]/35 text-[10px] uppercase tracking-[0.25em] font-medium hover:text-[#FDFBF7]/60 transition-colors" data-id="element-3327" data-mp="1beaitfcc6n1o">
                                  Maybe later, continue alone
                                </button>
                              </div>
                            </motion.div>
                          </motion.div>}
                      </AnimatePresence>
                    </>;
            })()}
              </motion.div>}

            {/* Step 11: Complete */}
            {step === 'complete' && <motion.div key="complete" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="space-y-8 text-center" data-id="element-3328" data-mp="2zb9zeo79dq4l">
                <div className="relative py-4" data-id="element-3329" data-mp="ljn75pi1f16">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.3),_transparent_70%)] blur-3xl" data-id="element-3330" data-mp="ncpk8t2xd3d" />
                  <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} className="w-20 h-20 mx-auto bg-[#D4AF37] rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.5)]" data-id="element-3331" data-mp="17jlqhmghp6cc">
                    <Check className="w-10 h-10 text-[#0B0F19]" data-id="element-3332" data-mp="olhultpm53sh" />
                  </motion.div>
                </div>

                <div className="space-y-2" data-id="element-3333" data-mp="tk2vlvcovrhz">
                  <h2 className="font-serif text-3xl text-[#FDFBF7]" data-id="element-3334" data-mp="tj19mvu4xklh">
                    Welcome to the Court
                  </h2>
                  <p className="text-[#FDFBF7]/60 text-sm" data-id="element-3335" data-mp="24zrjcoxrouju">
                    Your journey has officially begun.
                  </p>
                </div>

                <button onClick={onComplete} className="w-full py-4 bg-[#D4AF37] text-[#0B0F19] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2" data-id="element-3336" data-mp="28zrv21thern7">
                  Enter Dashboard <ArrowRight className="w-4 h-4" data-id="element-3337" data-mp="3kpsej71pjulw" />
                </button>
              </motion.div>}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>;
}
// Re-exporting icons for use in parent if needed
export { Repeat } from 'lucide-react';